// pages/ChatSpace/ChatSpaceInterface.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    CircularProgress,
    Snackbar,
    Alert,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    LinearProgress,
    Chip,
    Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsightsIcon from '@mui/icons-material/Insights';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import ChatSpaceMessageBubble from './ChatSpaceMessageBubble';
import axiosInstance from '../../utils/axiosInstance';
import { useChatbots } from '../../contexts/ChatbotContext';
import { v4 as uuidv4 } from 'uuid';

export default function ChatSpaceInterface({ sessionCode, onSessionChange, onCitationsChange, privacy }) {
    const { activeChatbot } = useChatbots();
    const [settings, setSettings] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [input, setInput] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [analyticsMode, setAnalyticsMode] = useState(false);
    const [imageGenerationMode, setImageGenerationMode] = useState(false);
    const [attachedImage, setAttachedImage] = useState(null);
    const [attachedImagePreview, setAttachedImagePreview] = useState(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const imageInputRef = useRef(null);

    const [internalSessionCode, setInternalSessionCode] = useState(null);
    const currentSessionCode = sessionCode !== undefined ? sessionCode : internalSessionCode;

    const isEmptyChat = messages.length === 0;

    // Add Menu State
    const [addMenuAnchor, setAddMenuAnchor] = useState(null);
    const addMenuOpen = Boolean(addMenuAnchor);

    // Dialog States
    const [websiteLinkDialogOpen, setWebsiteLinkDialogOpen] = useState(false);
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [audioDialogOpen, setAudioDialogOpen] = useState(false);

    // Website Link State
    const [websiteLink, setWebsiteLink] = useState('');
    const [isAddingWebsite, setIsAddingWebsite] = useState(false);

    // Document Upload State
    const [selectedDocuments, setSelectedDocuments] = useState(null);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);

    // Image Upload State
    const [selectedImages, setSelectedImages] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    // Audio Recording State
    const [isRecording, setIsRecording] = useState(false);
    const [isUploadingAudio, setIsUploadingAudio] = useState(false);
    const [audioStream, setAudioStream] = useState(null);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const audioChunksRef = useRef([]);
    const recordingTimeoutRef = useRef(null);
    const recordingIntervalRef = useRef(null);

    useEffect(() => {
        loadInitialSettings();
    }, []);

    useEffect(() => {
        if (settings) loadConversation(currentSessionCode);
    }, [currentSessionCode, settings]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadInitialSettings = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/api/chatspace/initial-data');
            const { settings: s } = response.data.data;
            setSettings(s);
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Could not load chat interface.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const loadConversation = async (sc) => {
        if (!settings) return;
        setIsLoading(true);
        onCitationsChange?.([]);

        if (!sc) {
            setMessages([]);
            setIsLoading(false);
            return;
        }

        try {
            const url = `/api/chatspace/initial-data?session_code=${encodeURIComponent(sc)}`;
            const response = await axiosInstance.get(url);
            const { history } = response.data.data;
            setMessages(history?.length > 0 ? history : []);
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Could not load conversation.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleInputChange = (e) => {
        setInput(e.target.value);
        const ta = e.target;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        // Create user message with optional attached image preview
        const userMessage = {
            id: `user-${Date.now()}`,
            message: input,
            is_from_user: true,
            attached_image: attachedImagePreview || null,
        };
        setMessages((prev) => [...prev, userMessage]);
        const currentInput = input;
        const currentAttachedImage = attachedImage;
        setInput('');
        setAttachedImage(null);
        setAttachedImagePreview(null);
        if (imageInputRef.current) imageInputRef.current.value = '';

        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        setIsSending(true);
        try {
            let response;

            if (imageGenerationMode) {
                // Use FormData for image generation mode
                const formData = new FormData();
                formData.append('message', currentInput);
                formData.append('privacy', privacy);
                formData.append('image_generation_mode', 'true');
                if (currentSessionCode) formData.append('session_code', currentSessionCode);
                if (currentAttachedImage) formData.append('image', currentAttachedImage);

                response = await axiosInstance.post('/api/chatspace/send-message', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                // Regular JSON payload for normal and analytics mode
                const payload = { message: currentInput, privacy, analytics_mode: analyticsMode };
                if (currentSessionCode) payload.session_code = currentSessionCode;

                response = await axiosInstance.post('/api/chatspace/send-message', payload);
            }

            const botMessage = response.data.data;
            setMessages((prev) => [...prev, botMessage]);

            if (!currentSessionCode && botMessage.session_code) {
                if (onSessionChange) {
                    onSessionChange(botMessage.session_code);
                } else {
                    setInternalSessionCode(botMessage.session_code);
                }
            }
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to get a response.', 'error');
            setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
        } finally {
            setIsSending(false);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!', 'success');
    };

    const handleCitationsClick = (msg) => {
        if (msg.citations?.length > 0) {
            onCitationsChange?.(msg.citations);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    // Add Menu Handlers
    const handleAddMenuClick = (event) => {
        setAddMenuAnchor(event.currentTarget);
    };

    const handleAddMenuClose = () => {
        setAddMenuAnchor(null);
    };

    // Website Link Handlers
    const handleOpenWebsiteLinkDialog = () => {
        handleAddMenuClose();
        setWebsiteLink('');
        setWebsiteLinkDialogOpen(true);
    };

    const handleCloseWebsiteLinkDialog = () => {
        setWebsiteLinkDialogOpen(false);
        setWebsiteLink('');
    };

    const handleAddWebsiteLink = async () => {
        if (!websiteLink.trim()) {
            showSnackbar('Please enter a website link.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsAddingWebsite(true);
        try {
            const params = { chatbot_id: activeChatbot.id };
            let linkToAdd = websiteLink.trim();
            if (!/^(https?:\/\/|mailto:|tel:)/i.test(linkToAdd)) {
                linkToAdd = 'https://' + linkToAdd;
            }
            const response = await axiosInstance.post(
                '/add-website-link',
                { website_link: linkToAdd },
                { params }
            );
            showSnackbar(response.data.message || 'Website link added and training started!', 'success');
            handleCloseWebsiteLinkDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to add website link.', 'error');
        } finally {
            setIsAddingWebsite(false);
        }
    };

    // Document Upload Handlers
    const handleOpenDocumentDialog = () => {
        handleAddMenuClose();
        setSelectedDocuments(null);
        setDocumentDialogOpen(true);
    };

    const handleCloseDocumentDialog = () => {
        setDocumentDialogOpen(false);
        setSelectedDocuments(null);
    };

    const handleDocumentFileChange = (event) => {
        setSelectedDocuments(event.target.files);
    };

    const handleUploadDocument = async () => {
        if (!selectedDocuments || selectedDocuments.length === 0) {
            showSnackbar('Please select document(s) to upload.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsUploadingDocument(true);
        try {
            const formData = new FormData();
            for (let i = 0; i < selectedDocuments.length; i++) {
                formData.append('files', selectedDocuments[i]);
            }
            formData.append('category_ids', JSON.stringify([]));

            const params = { chatbot_id: activeChatbot.id };
            const response = await axiosInstance.post('/upload-private-data', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params,
            });
            showSnackbar(response.data.message || 'Document(s) uploaded and training started!', 'success');
            handleCloseDocumentDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to upload document(s).', 'error');
        } finally {
            setIsUploadingDocument(false);
        }
    };

    // Image Upload Handlers
    const handleOpenImageDialog = () => {
        handleAddMenuClose();
        setSelectedImages(null);
        setImageDialogOpen(true);
    };

    const handleCloseImageDialog = () => {
        setImageDialogOpen(false);
        setSelectedImages(null);
    };

    const handleImageFileChange = (event) => {
        setSelectedImages(event.target.files);
    };

    const handleUploadImage = async () => {
        if (!selectedImages || selectedImages.length === 0) {
            showSnackbar('Please select image(s) to upload.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsUploadingImage(true);
        try {
            const formData = new FormData();
            for (let i = 0; i < selectedImages.length; i++) {
                formData.append('files', selectedImages[i]);
            }

            const params = { chatbot_id: activeChatbot.id };
            const response = await axiosInstance.post('/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params,
            });
            showSnackbar(response.data.message || 'Image(s) uploaded and training started!', 'success');
            handleCloseImageDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to upload image(s).', 'error');
        } finally {
            setIsUploadingImage(false);
        }
    };

    // Audio Recording Handlers
    const handleOpenAudioDialog = () => {
        handleAddMenuClose();
        setRecordingTime(0);
        setAudioDialogOpen(true);
    };

    const handleCloseAudioDialog = () => {
        if (isRecording) {
            stopRecording();
        }
        setAudioDialogOpen(false);
        setRecordingTime(0);
    };

    const startRecording = async () => {
        try {
            setIsRecording(true);
            setRecordingTime(0);
            audioChunksRef.current = [];

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { channelCount: 1 },
                video: false,
            });

            const recorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
            });

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = async () => {
                stream.getTracks().forEach((track) => track.stop());
                setIsRecording(false);
                setAudioStream(null);

                if (recordingTimeoutRef.current) {
                    clearTimeout(recordingTimeoutRef.current);
                    recordingTimeoutRef.current = null;
                }
                if (recordingIntervalRef.current) {
                    clearInterval(recordingIntervalRef.current);
                    recordingIntervalRef.current = null;
                }

                if (audioChunksRef.current.length > 0) {
                    await handleUploadAudio(audioChunksRef.current);
                }
            };

            setMediaRecorder(recorder);
            setAudioStream(stream);
            recorder.start(100);

            // Auto-stop after 2 minutes
            recordingTimeoutRef.current = setTimeout(() => {
                if (recorder.state === 'recording') {
                    recorder.stop();
                }
            }, 120000);

            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime((prev) => prev + 1);
            }, 1000);
        } catch (err) {
            console.error('Error starting recording:', err);
            showSnackbar(`Failed to start recording: ${err.message}`, 'error');
            setIsRecording(false);
        }
    };

    const stopRecording = () => {
        if (recordingTimeoutRef.current) {
            clearTimeout(recordingTimeoutRef.current);
            recordingTimeoutRef.current = null;
        }
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
        if (audioStream) {
            audioStream.getTracks().forEach((track) => track.stop());
            setAudioStream(null);
        }
    };

    const handleUploadAudio = async (chunks) => {
        if (!chunks || chunks.length === 0) {
            showSnackbar('No audio recorded.', 'error');
            return;
        }

        if (!activeChatbot?.id) {
            showSnackbar('No active chatbot selected.', 'error');
            return;
        }

        setIsUploadingAudio(true);
        try {
            const audioBlob = new Blob(chunks, { type: 'audio/webm' });
            const formData = new FormData();
            formData.append('file', audioBlob, `recording_${uuidv4()}.webm`);
            formData.append('lang_type', 'en-US');

            const params = { chatbot_id: activeChatbot.id };
            const response = await axiosInstance.post('/upload-voice', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params,
            });
            showSnackbar(response.data.message || 'Audio uploaded and training started!', 'success');
            handleCloseAudioDialog();
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to upload audio.', 'error');
        } finally {
            setIsUploadingAudio(false);
        }
    };

    // Cleanup recording on unmount
    useEffect(() => {
        return () => {
            if (recordingTimeoutRef.current) {
                clearTimeout(recordingTimeoutRef.current);
            }
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
            if (audioStream) {
                audioStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [audioStream]);

    if (isLoading && !settings) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!settings) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography color="error">Chat unavailable.</Typography>
            </Box>
        );
    }

    // Quick action chips configuration
    const quickActions = [
        { id: 'analytics', label: 'Analytics', icon: <PsychologyIcon sx={{ fontSize: 16 }} /> },
        { id: 'image-generation', label: 'Image Generation', icon: <AutoFixHighIcon sx={{ fontSize: 16 }} /> },
    ];

    const handleQuickAction = (actionId) => {
        if (actionId === 'analytics') {
            // Activate analytics mode, deactivate image generation
            setAnalyticsMode(true);
            setImageGenerationMode(false);
            setAttachedImage(null);
            setAttachedImagePreview(null);
            textareaRef.current?.focus();
        } else if (actionId === 'image-generation') {
            // Activate image generation mode, deactivate analytics
            setImageGenerationMode(true);
            setAnalyticsMode(false);
            textareaRef.current?.focus();
        }
    };

    // Handle image attachment for image generation mode
    const handleImageAttachment = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setAttachedImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setAttachedImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeAttachedImage = () => {
        setAttachedImage(null);
        setAttachedImagePreview(null);
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    };

    const inputArea = (
        <Box sx={{ width: '100%' }}>
            <form onSubmit={handleSendMessage} style={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.75,
                        '&:focus-within': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    {/* Add Button */}
                    <IconButton
                        onClick={handleAddMenuClick}
                        disabled={isSending}
                        sx={{
                            bgcolor: 'grey.200',
                            color: 'grey.700',
                            borderRadius: 2,
                            width: 36,
                            height: 36,
                            '&:hover': { bgcolor: 'grey.300' },
                            '&:disabled': { bgcolor: 'grey.100', color: 'grey.400' },
                        }}
                    >
                        <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    {/* Analytics Mode Toggle - Shows inside input when active */}
                    {analyticsMode && (
                        <Box
                            onClick={() => setAnalyticsMode(false)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 6,
                                cursor: 'pointer',
                                bgcolor: 'rgba(124, 58, 237, 0.1)',
                                border: '1px solid',
                                borderColor: 'primary.main',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'rgba(124, 58, 237, 0.15)',
                                },
                            }}
                        >
                            <PsychologyIcon
                                sx={{
                                    fontSize: 18,
                                    color: 'primary.main',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: 'primary.main',
                                    fontSize: '0.8125rem',
                                }}
                            >
                                Analytics
                            </Typography>
                        </Box>
                    )}

                    {/* Image Generation Mode Toggle - Shows inside input when active */}
                    {imageGenerationMode && (
                        <Box
                            onClick={() => {
                                setImageGenerationMode(false);
                                setAttachedImage(null);
                                setAttachedImagePreview(null);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 6,
                                cursor: 'pointer',
                                bgcolor: 'rgba(236, 72, 153, 0.1)',
                                border: '1px solid',
                                borderColor: '#ec4899',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'rgba(236, 72, 153, 0.15)',
                                },
                            }}
                        >
                            <AutoFixHighIcon
                                sx={{
                                    fontSize: 18,
                                    color: '#ec4899',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: '#ec4899',
                                    fontSize: '0.8125rem',
                                }}
                            >
                                Image Generation
                            </Typography>
                        </Box>
                    )}

                    {/* Attached Image Preview (for Image Generation mode) */}
                    {imageGenerationMode && attachedImagePreview && (
                        <Box
                            sx={{
                                position: 'relative',
                                width: 48,
                                height: 48,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'grey.300',
                            }}
                        >
                            <img
                                src={attachedImagePreview}
                                alt="Attached"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={removeAttachedImage}
                                sx={{
                                    position: 'absolute',
                                    top: -6,
                                    right: -6,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    width: 18,
                                    height: 18,
                                    '&:hover': { bgcolor: 'error.dark' },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Box>
                    )}

                    {/* Hidden file input for image attachment */}
                    <input
                        type="file"
                        ref={imageInputRef}
                        accept="image/*"
                        onChange={handleImageAttachment}
                        style={{ display: 'none' }}
                    />

                    {/* Textarea */}
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="How can I help you today?"
                        rows={1}
                        disabled={isSending}
                        style={{
                            flex: 1,
                            resize: 'none',
                            outline: 'none',
                            border: 'none',
                            background: 'transparent',
                            padding: '8px 12px',
                            fontSize: '0.9375rem',
                            fontFamily: 'inherit',
                            maxHeight: 200,
                            minHeight: 24,
                            lineHeight: 1.5,
                            color: '#1f2937',
                        }}
                    />

                    {/* Character count (optional display) */}
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'grey.400',
                            fontSize: '0.75rem',
                            minWidth: 20,
                            textAlign: 'center',
                        }}
                    >
                        {input.length > 0 ? input.length : ''}
                    </Typography>

                    {/* Send Button */}
                    <IconButton
                        type="submit"
                        disabled={isSending || !input.trim()}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            '&:hover': { bgcolor: 'primary.dark' },
                            '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                        }}
                    >
                        <SendIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </form>

            {/* Quick Action Chips */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1.5,
                    justifyContent: 'flex-start',
                }}
            >
                {quickActions
                    .filter((action) => {
                        if (action.id === 'analytics' && analyticsMode) return false;
                        if (action.id === 'image-generation' && imageGenerationMode) return false;
                        return true;
                    })
                    .map((action) => (
                        <Chip
                            key={action.id}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <span style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center' }}>{action.icon}</span>
                                    <span>{action.label}</span>
                                </Box>
                            }
                            onClick={() => handleQuickAction(action.id)}
                            disabled={isSending}
                            size="small"
                            sx={{
                                bgcolor: 'white',
                                border: '1px solid',
                                borderColor: 'grey.300',
                                borderRadius: 2,
                                px: 0.5,
                                '&:hover': {
                                    bgcolor: 'grey.50',
                                    borderColor: 'grey.400',
                                },
                                '& .MuiChip-label': {
                                    px: 1,
                                    py: 0.5,
                                    fontSize: '0.8125rem',
                                    fontWeight: 400,
                                },
                            }}
                        />
                    ))}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                }}
            >
                {/* Empty state */}
                {isEmptyChat && !isLoading ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: 4 }}>
                        <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
                            <Avatar
                                sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: settings.primary_color,
                                    color: settings.secondary_color,
                                    mx: 'auto',
                                    mb: 3,
                                    boxShadow: 3,
                                    borderRadius: 3,
                                }}
                            >
                                <AutoAwesomeIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Typography variant="h5" fontWeight={600} gutterBottom>
                                {settings.greeting_text || 'How can I help you today?'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                Ask me anything, I'm here to help.
                            </Typography>
                            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                                {inputArea}
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {/* Messages */}
                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                bgcolor: 'grey.50',
                                // Modern scrollbar styling
                                '&::-webkit-scrollbar': {
                                    width: 8,
                                },
                                '&::-webkit-scrollbar-track': {
                                    bgcolor: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    bgcolor: 'grey.300',
                                    borderRadius: 4,
                                    '&:hover': {
                                        bgcolor: 'grey.400',
                                    },
                                },
                                // Firefox scrollbar
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#d1d5db transparent',
                            }}
                        >
                            <Box sx={{ width: '90%', mx: 'auto', py: 3 }}>
                                {messages.map((msg) => (
                                    <ChatSpaceMessageBubble
                                        key={msg.id}
                                        msg={msg}
                                        settings={settings}
                                        onCitations={() => handleCitationsClick(msg)}
                                        onCopy={handleCopy}
                                    />
                                ))}

                                {isSending && (
                                    <Box sx={{ display: 'flex', gap: 2, px: 2, py: 3 }}>
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                bgcolor: settings.primary_color,
                                                color: settings.secondary_color,
                                            }}
                                        >
                                            <SmartToyIcon sx={{ fontSize: 20 }} />
                                        </Avatar>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            {[0, 150, 300].map((delay) => (
                                                <Box
                                                    key={delay}
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: 'grey.400',
                                                        animation: 'bounce 1.4s infinite both',
                                                        animationDelay: `${delay}ms`,
                                                        '@keyframes bounce': {
                                                            '0%, 80%, 100%': { transform: 'scale(0)' },
                                                            '40%': { transform: 'scale(1)' },
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                <div ref={messagesEndRef} />
                            </Box>
                        </Box>

                        {/* Footer input */}
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0, px: 3, py: 2 }}>
                            <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                                {inputArea}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Add Menu */}
            <Menu
                anchorEl={addMenuAnchor}
                open={addMenuOpen}
                onClose={handleAddMenuClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {imageGenerationMode ? (
                    // Only show Attach Image option in Image Generation mode
                    <MenuItem
                        onClick={() => {
                            handleAddMenuClose();
                            imageInputRef.current?.click();
                        }}
                    >
                        <ListItemIcon>
                            <ImageIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Attach Image (Optional)</ListItemText>
                    </MenuItem>
                ) : (
                    // Show all options in normal mode
                    <>
                        <MenuItem onClick={handleOpenWebsiteLinkDialog}>
                            <ListItemIcon>
                                <LinkIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Add Website Link</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleOpenDocumentDialog}>
                            <ListItemIcon>
                                <DescriptionIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Upload Document</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleOpenImageDialog}>
                            <ListItemIcon>
                                <ImageIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Upload Image</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleOpenAudioDialog}>
                            <ListItemIcon>
                                <MicIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Upload Audio</ListItemText>
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Website Link Dialog */}
            <Dialog open={websiteLinkDialogOpen} onClose={handleCloseWebsiteLinkDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Add Website Link</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter a website URL to scrape and train your chatbot. The content will be automatically processed and added to your knowledge base.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Website Link"
                        variant="outlined"
                        value={websiteLink}
                        onChange={(e) => setWebsiteLink(e.target.value)}
                        placeholder="https://example.com"
                        disabled={isAddingWebsite}
                        sx={{ mt: 1 }}
                    />
                    {isAddingWebsite && <LinearProgress sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWebsiteLinkDialog} disabled={isAddingWebsite}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddWebsiteLink}
                        variant="contained"
                        disabled={isAddingWebsite || !websiteLink.trim()}
                        startIcon={isAddingWebsite ? <CircularProgress size={16} /> : <LinkIcon />}
                    >
                        {isAddingWebsite ? 'Adding...' : 'Add Link'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Upload Dialog */}
            <Dialog open={documentDialogOpen} onClose={handleCloseDocumentDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Document</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload document files (.pdf, .doc, .docx, .txt, .csv, .xls, .xlsx) to train your chatbot.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isUploadingDocument}
                            startIcon={<CloudUploadIcon />}
                        >
                            Choose Files
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                                onChange={handleDocumentFileChange}
                                hidden
                            />
                        </Button>
                        {selectedDocuments && (
                            <Typography variant="body2" color="text.secondary">
                                {selectedDocuments.length} file(s) selected
                            </Typography>
                        )}
                    </Box>
                    {isUploadingDocument && <LinearProgress sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDocumentDialog} disabled={isUploadingDocument}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUploadDocument}
                        variant="contained"
                        disabled={isUploadingDocument || !selectedDocuments}
                        startIcon={isUploadingDocument ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                    >
                        {isUploadingDocument ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Upload Dialog */}
            <Dialog open={imageDialogOpen} onClose={handleCloseImageDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Image</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload image files (.jpg, .jpeg, .png) to train your chatbot with visual content.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={isUploadingImage}
                            startIcon={<ImageIcon />}
                        >
                            Choose Images
                            <input
                                type="file"
                                multiple
                                accept=".jpg,.jpeg,.png"
                                onChange={handleImageFileChange}
                                hidden
                            />
                        </Button>
                        {selectedImages && (
                            <Typography variant="body2" color="text.secondary">
                                {selectedImages.length} image(s) selected
                            </Typography>
                        )}
                    </Box>
                    {isUploadingImage && <LinearProgress sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseImageDialog} disabled={isUploadingImage}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUploadImage}
                        variant="contained"
                        disabled={isUploadingImage || !selectedImages}
                        startIcon={isUploadingImage ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                    >
                        {isUploadingImage ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Audio Recording Dialog */}
            <Dialog open={audioDialogOpen} onClose={handleCloseAudioDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Record Audio</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Record audio to transcribe and train your chatbot. Maximum recording time is 2 minutes.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Button
                            variant="contained"
                            color={isRecording ? 'error' : 'primary'}
                            onClick={isRecording ? stopRecording : startRecording}
                            disabled={isUploadingAudio}
                            startIcon={isRecording ? <StopCircleIcon /> : <MicIcon />}
                            size="large"
                        >
                            {isRecording ? 'Stop Recording' : 'Start Recording'}
                        </Button>
                        {isRecording && (
                            <Typography variant="body1" color="error">
                                Recording: {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:
                                {(recordingTime % 60).toString().padStart(2, '0')}
                            </Typography>
                        )}
                        {isUploadingAudio && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">Transcribing and uploading...</Typography>
                            </Box>
                        )}
                    </Box>
                    {isUploadingAudio && <LinearProgress sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAudioDialog} disabled={isRecording || isUploadingAudio}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
